import React from 'react';
import { Calendar, Clock, Users, ArrowRight, Zap, Target, Briefcase } from 'lucide-react';

const MycalLanding = () => {
  return (
    <div className="min-h-screen bg-gray-50">
      {/* Header */}
      <header className="bg-white border-b-2 border-dark shadow-retro">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center py-6">
            <div className="flex items-center">
              <Calendar className="h-8 w-8 text-primary mr-3" />
              <span className="text-2xl font-bold text-dark">mycal.live</span>
            </div>
            <div className="hidden sm:flex items-center space-x-8">
              <a href="#features" className="text-gray-600 hover:text-primary transition-colors">Features</a>
              <a href="#how-it-works" className="text-gray-600 hover:text-primary transition-colors">How it works</a>
              <a href="#pricing" className="text-gray-600 hover:text-primary transition-colors">Pricing</a>
            </div>
          </div>
        </div>
      </header>

      {/* Hero Section */}
      <section className="py-20 px-4 sm:px-6 lg:px-8">
        <div className="max-w-4xl mx-auto text-center">
          <h1 className="text-5xl md:text-6xl font-bold text-dark mb-6 leading-tight">
            The Scheduling Tool That 
            <span className="text-primary block">Multiplies Time</span>
          </h1>
          <p className="text-xl md:text-2xl text-gray-600 mb-8 max-w-3xl mx-auto">
            While other tools give you your time back, mycal.live gives you 10x more output. 
            Same calendar. Different results.
          </p>
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <button className="bg-primary hover:bg-primary/90 text-white font-bold px-8 py-4 rounded-full border-2 border-dark shadow-retro hover:shadow-none hover:-translate-y-1 transition-all">
              Get Your Link
              <ArrowRight className="inline-block ml-2 h-5 w-5" />
            </button>
            <button className="bg-white hover:bg-gray-50 text-dark font-bold px-8 py-4 rounded-full border-2 border-dark shadow-retro hover:shadow-none hover:-translate-y-1 transition-all">
              See How It Works
            </button>
          </div>
        </div>
      </section>

      {/* Stats Section */}
      <section className="py-16 bg-primary/10">
        <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid md:grid-cols-3 gap-8 text-center">
            <div className="bg-white p-8 rounded-xl border-2 border-dark shadow-retro">
              <div className="text-4xl font-bold text-primary mb-2">10x</div>
              <div className="text-gray-600">More productive meetings</div>
            </div>
            <div className="bg-white p-8 rounded-xl border-2 border-dark shadow-retro">
              <div className="text-4xl font-bold text-primary mb-2">100hrs</div>
              <div className="text-gray-600">Weekly output potential</div>
            </div>
            <div className="bg-white p-8 rounded-xl border-2 border-dark shadow-retro">
              <div className="text-4xl font-bold text-primary mb-2">0%</div>
              <div className="text-gray-600">Wasted time</div>
            </div>
          </div>
        </div>
      </section>

      {/* Features Section */}
      <section id="features" className="py-20 px-4 sm:px-6 lg:px-8">
        <div className="max-w-6xl mx-auto">
          <div className="text-center mb-16">
            <h2 className="text-4xl font-bold text-dark mb-4">
              Built for People Who Have Too Much to Do
            </h2>
            <p className="text-xl text-gray-600">
              And not enough hours in the day
            </p>
          </div>
          
          <div className="grid md:grid-cols-3 gap-8">
            <div className="bg-white p-8 rounded-xl border-2 border-dark shadow-retro hover:shadow-none hover:-translate-y-1 transition-all">
              <Zap className="h-12 w-12 text-primary mb-4" />
              <h3 className="text-xl font-bold text-dark mb-3">Unreasonable Speed</h3>
              <p className="text-gray-600">
                Meetings become movement. You'll wonder how things got done so fast.
              </p>
            </div>
            
            <div className="bg-white p-8 rounded-xl border-2 border-dark shadow-retro hover:shadow-none hover:-translate-y-1 transition-all">
              <Target className="h-12 w-12 text-primary mb-4" />
              <h3 className="text-xl font-bold text-dark mb-3">Zero Wasted Time</h3>
              <p className="text-gray-600">
                Every meeting creates outcomes. Follow-ups happen. Next steps move.
              </p>
            </div>
            
            <div className="bg-white p-8 rounded-xl border-2 border-dark shadow-retro hover:shadow-none hover:-translate-y-1 transition-all">
              <Briefcase className="h-12 w-12 text-primary mb-4" />
              <h3 className="text-xl font-bold text-dark mb-3">Feels Like Magic</h3>
              <p className="text-gray-600">
                There's no other way to explain it. You'll see.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* How It Works */}
      <section id="how-it-works" className="py-20 bg-gray-100 px-4 sm:px-6 lg:px-8">
        <div className="max-w-4xl mx-auto text-center">
          <h2 className="text-4xl font-bold text-dark mb-8">
            Your Calendar Is Lying to You
          </h2>
          <p className="text-xl text-gray-600 mb-12">
            Traditional scheduling tools are built for availability. mycal.live is built for output.
          </p>
          
          <div className="bg-white p-12 rounded-xl border-2 border-dark shadow-retro">
            <div className="space-y-8">
              <div className="flex items-center justify-center space-x-4">
                <div className="bg-primary/10 p-4 rounded-full">
                  <Calendar className="h-8 w-8 text-primary" />
                </div>
                <ArrowRight className="h-6 w-6 text-gray-400" />
                <div className="bg-primary/10 p-4 rounded-full">
                  <Clock className="h-8 w-8 text-primary" />
                </div>
                <ArrowRight className="h-6 w-6 text-gray-400" />
                <div className="bg-primary/10 p-4 rounded-full">
                  <Users className="h-8 w-8 text-primary" />
                </div>
              </div>
              
              <p className="text-lg text-gray-600">
                This isn't about booking time. It's about unlocking leverage.
                <br />
                What happens after someone books with you isn't just more efficient — it's exponential.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Testimonial */}
      <section className="py-20 px-4 sm:px-6 lg:px-8">
        <div className="max-w-4xl mx-auto text-center">
          <div className="bg-white p-12 rounded-xl border-2 border-dark shadow-retro">
            <blockquote className="text-2xl font-medium text-dark mb-6">
              "It's not a calendar app. It's an unfair advantage."
            </blockquote>
            <cite className="text-gray-600">
              — Someone who now gets more done in a week than most do in a quarter
            </cite>
          </div>
        </div>
      </section>

      {/* 100 Hours Section */}
      <section className="py-20 bg-primary/5 px-4 sm:px-6 lg:px-8">
        <div className="max-w-4xl mx-auto text-center">
          <h2 className="text-4xl font-bold text-dark mb-8">
            What Would You Do With 100 Hours This Week?
          </h2>
          <p className="text-xl text-gray-600 mb-8">
            Most people work 40. Some grind to hit 60. mycal.live users routinely punch above 100.
          </p>
          <p className="text-lg text-gray-600 mb-12">
            We're not saying you'll clone yourself.<br />
            We're just saying it won't feel like you're working alone.
          </p>
          
          <button className="bg-primary hover:bg-primary/90 text-white font-bold px-12 py-4 rounded-full border-2 border-dark shadow-retro hover:shadow-none hover:-translate-y-1 transition-all text-xl">
            Get Started
            <ArrowRight className="inline-block ml-2 h-6 w-6" />
          </button>
        </div>
      </section>

      {/* Footer */}
      <footer className="bg-dark text-white py-12 px-4 sm:px-6 lg:px-8">
        <div className="max-w-6xl mx-auto text-center">
          <div className="flex items-center justify-center mb-6">
            <Calendar className="h-8 w-8 text-primary mr-3" />
            <span className="text-2xl font-bold">mycal.live</span>
          </div>
          <p className="text-gray-400 mb-4">
            Not a productivity hack. Not a gimmick. Just a calendar with consequences.
          </p>
          <p className="text-gray-500 text-sm">
            © 2025 mycal.live — Time, multiplied.
          </p>
        </div>
      </footer>
    </div>
  );
};

export default MycalLanding;